import { Group, Radio, Stack } from '@mantine/core'
import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { FunctionComponent, MouseEvent, ReactNode } from 'react'
import { Sizes } from '~/assets/style/tokens'
import { Icon } from '~/components/Icon'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { UpgradeChip } from '~/modules/monetization/upgrade/UpgradeChip'
import { UpgradeModal } from '~/modules/monetization/upgrade/UpgradeModal'

type Props = {
  value: string
  title: string
  description: ReactNode
  isSuggested?: boolean
  /** Discouraged, not blocked: the card is warning-tinted but stays selectable. */
  isDeprecated?: boolean
  /** Present disables the card. Rendered inline, not as a tooltip: a disabled card swallows hover. */
  disabledReason?: string
  /**
   * The offers that would open this choice, where `disabledReason` means "not yet" rather than "not
   * allowed" or "not applicable". It changes what the card says and what it does: a crown and the word
   * Upgrade instead of a padlock and Unavailable, and a click that asks for the offer rather than nothing.
   *
   * An empty list still pitches. Spark is switched on per scope by the Spark team and names no product to
   * buy, and the catalog already decided that an unavailable entry is an unavailable entry: same card,
   * same CTA, whether or not an offer can be named. `undefined` is the one value that does not pitch.
   *
   * The card stays enabled for that click — a disabled control emits no events, so a padlocked card could
   * only ever be a dead end — and the click is intercepted before Mantine can select the value.
   */
  upgradeOffers?: UpgradeOffer[]
  /** Unavailable because it already exists, not because something is missing: shown as done, not blocked. */
  isAlreadyCreated?: boolean
  /** Pubstack-internal: badged so staff never mistake it for a choice the customer is being offered. */
  isInternal?: boolean
  /** Available to Pubstack staff alone for now (the product is onboarded but its users have no access): shield and blue border. */
  isStaffOnly?: boolean
  trackingLabel: TrackingLabel
}

const BADGE = 'inline-flex shrink-0 items-center gap-1 rounded-xl px-2 py-0.5 text-xs leading-5 font-medium'
const BASE = 'group box-border! w-full rounded-sm border bg-white p-3 enabled:cursor-pointer disabled:cursor-not-allowed'
const NEUTRAL = 'border-platinum enabled:hover:border-silver data-[checked]:border-king data-[checked]:bg-ghost data-[checked]:enabled:hover:border-king'
const DEPRECATED = 'border-warning-ink bg-warning-soft data-[checked]:border-warning-ink data-[checked]:bg-warning-soft'
const INTERNAL = 'border-lava bg-lava-soft data-[checked]:border-lava data-[checked]:bg-lava-soft'
const STAFF_ONLY = 'border-king enabled:hover:border-king data-[checked]:border-king data-[checked]:bg-ghost'
/** Enabled, so it can be pressed, but never selected — it reads as the offer it is, not as a choice. */
const PITCHED = 'border-platinum hover:border-king cursor-pointer'

/**
 * One choice in a radio group, as a card carrying its own title, description and — when it is out of
 * reach — the reason why. Unavailable choices stay visible: seeing what exists is half the decision.
 */
export const MethodCard: FunctionComponent<Props> = ({
  value,
  title,
  description,
  isSuggested,
  isDeprecated,
  disabledReason,
  isAlreadyCreated,
  isInternal,
  isStaffOnly,
  upgradeOffers,
  trackingLabel,
}) => {
  const modal = useGlobalModal()
  const isOutOfReach = !!disabledReason
  const pitches = isOutOfReach && upgradeOffers !== undefined
  // Out of reach either way; only a card with nothing to sell is handed to the browser as `disabled`.
  const disabled = isOutOfReach && !pitches
  const showAsDeprecated = isDeprecated && !isOutOfReach

  /* `bare` hands the modal's surface to the card, so this is the same offer the Catalog and Ad units show.
     The reason travels as its context: it names the thing just reached for, which the offer alone cannot. */
  const showOffer = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    modal.open(UpgradeModal, { offers: upgradeOffers ?? [], context: disabledReason }, { bare: true })
  }

  return (
    <Radio.Card
      value={value}
      data-tracking={trackingLabel}
      disabled={disabled}
      /* Capture, so the click is taken before Mantine reads it as a selection: the choice is not this
         reader's to make yet, and the offer is what they get instead. */
      onClickCapture={pitches ? showOffer : undefined}
      className={`${BASE} ${pitches ? PITCHED : isInternal ? INTERNAL : isStaffOnly && !isOutOfReach ? STAFF_ONLY : showAsDeprecated ? DEPRECATED : NEUTRAL}`}
    >
      <Stack gap={Sizes[4]}>
        <Group gap={Sizes[8]} justify="space-between" wrap="nowrap">
          <Group gap={Sizes[8]} wrap="nowrap">
            <Radio.Indicator />
            <span className="text-base leading-[1.625rem] font-bold text-ink group-disabled:text-silver">{title}</span>
          </Group>
          {isOutOfReach ? (
            isAlreadyCreated ? (
              <span className={`${BADGE} bg-success/15 text-ink`}>
                <Icon name={'check'} width={Sizes[12]} fill={'var(--color-success)'} /> Created
              </span>
            ) : pitches ? (
              /* A crown and an act, not a padlock and a verdict: nothing here is forbidden, it is unbought
                 — the one thing a lock does not say — and the word is what tells the reader the card still
                 does something when pressed. */
              <UpgradeChip className={`${BADGE} bg-king-soft`}>Upgrade</UpgradeChip>
            ) : (
              <span className={`${BADGE} bg-cloud text-ink`}>
                <Icon name={'lock'} width={Sizes[12]} /> Unavailable
              </span>
            )
          ) : showAsDeprecated ? (
            <span className={`${BADGE} border border-warning-ink bg-white text-warning-ink`}>
              <Icon name={'warning'} width={Sizes[12]} fill={'var(--color-warning-ink)'} /> Deprecated
            </span>
          ) : isInternal ? (
            <span className={`${BADGE} border-platinum text-ink border bg-white`}>
              <Icon name={'admin'} width={Sizes[12]} /> Pubstack only
            </span>
          ) : isStaffOnly ? (
            <span title={'Pubstack staff only'} className={`${BADGE} border border-king bg-white text-king`}>
              <Icon name={'admin'} width={Sizes[12]} /> Staff only
            </span>
          ) : (
            isSuggested && (
              <span className={`${BADGE} border border-king-soft bg-king-soft text-king`}>
                <Icon name={'star'} width={Sizes[12]} /> Suggested
              </span>
            )
          )}
        </Group>
        <p className="m-0 pl-6 text-sm leading-[1.375rem] text-ink group-disabled:text-silver">{description}</p>
        {isOutOfReach && <p className="m-0 pl-6 text-sm leading-[1.375rem] text-hurricane">{disabledReason}</p>}
      </Stack>
    </Radio.Card>
  )
}
